import React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import cn from "classnames";
import { bytesToSize, everyTrue, hasTrue, createShortPdfName } from "@/helper/helper";
import LoadingDots from "@/components/common/LoadingDots/LoadingDots";
import { AiOutlineFilePdf } from "react-icons/ai";
import { forRender, maxFileUploadSize } from "@/constant";
import { useUserPdf } from "@/states/qr_types/pdf.state";
import { useConfig, useIsRequest } from "@/states/common.state";
import { BiErrorCircle } from "react-icons/bi";
import Error from "@/components/common/Error/Error";
import useFileUpload from "@/hooks/useFileUpload";
import { useHandleGenerateResult } from "@/hooks/useHandleGenerateResult";
import { useSignUpModal } from "@/states/ui.state";
import useGenerateHomeQr from "@/components/page-modules/home-page/hooks/useGenerateHomeQr";
import { HomePageError } from "@/components/common/HomePageError";
import classes from "./PdfHomepage.module.scss";
import { useTranslations } from "next-intl";
import useGuestFile from "@/hooks/common/useGuestFile";
import { When } from "@/components/common/When";
import IFThen from "@/components/common/IFThen";
import { useMediaQuery } from "react-responsive";
import { FiUploadCloud } from "react-icons/fi";
import { HiOutlineRefresh } from "react-icons/hi";
import { useQrShowState } from "@/states/qr.state";
import { UserPdfType } from "@/types/user.types";
const Pdf = () => {
  // requests
  const {
    data,
    setUserPdf,
    setDelete,
    destroyUserPdf
  } = useUserPdf();
  const {
    payload,
    config,
    setFiles
  } = useConfig();
  const {
    handleGenerateResult
  } = useHandleGenerateResult();
  const {
    uploadFileData,
    uploadFile,
    isLoadingUploadFile
  } = useGuestFile();
  const {
    mutateHomeQr,
    isSuccess
  } = useGenerateHomeQr();
  const isMobile = useMediaQuery({
    maxWidth: 1025
  });
  const t = useTranslations("QrTypes.pdf");
  const t2 = useTranslations("Logo");
  const {
    dashboardQr,
    removeDashboardQr
  } = useQrShowState();

  // states
  const {
    setIsRequest
  } = useIsRequest();
  const [file, setFile] = useState<{
    file: File | null;
    name: string;
    size: string;
    fromOther: boolean;
  }>();

  // hooks
  const signupModalState = useSignUpModal();

  // errors
  const {
    errorMessage,
    setErrorMessage,
    handleFileRejectionErrors,
    hasFileUploadErr
  } = useFileUpload(t);
  const [errors, setErrors] = useState<Record<string, any> | null>(null);
  const {
    invalidType,
    largeFile
  } = errorMessage;
  const fileError = errors?.["files.pdf"];
  const [loadingState, setLoadingState] = useState<UserPdfType>({
    name: '',
    size: ''
  });
  const [isDragging, setIsDragging] = useState(false); // Hover state

  useEffect(() => {
    if (signupModalState.isTriedDownload) {
      queueMicrotask(() => {
        setErrors(null);
        setTimeout(() => {
          setErrors(dashboardQr?.["validation_errors"] ?? null);
        }, 200);
      });
    }
  }, [payload, file]);
  useEffect(() => {
    if (fileError) setErrorMessage({
      invalidType: "",
      largeFile: ""
    });
  }, [fileError]);
  useEffect(() => {
    if (errors) {
      window.scrollTo({
        behavior: "smooth",
        top: 0
      });
    }
  }, [errors]);
  useEffect(() => {
    setIsRequest(true);
    setDelete("name");
    handleGenerateResult(null);
  }, []);
  let upload = 0;
  const {
    getRootProps,
    getInputProps
  } = useDropzone({
    noClick: isLoadingUploadFile,
    accept: {
      "application/pdf": [".pdf"]
    },
    maxSize: maxFileUploadSize,
    multiple: false,
    onDrop: (e: File[], fileRejections: unknown[]) => {
      setErrorMessage({
        largeFile: "",
        invalidType: ""
      });
      setErrors(null);
      setFile({
        file: null,
        name: "",
        size: "",
        fromOther: false
      });
      // fixme no need for destroyUserPdf?
      destroyUserPdf();
      setFiles({
        pdf: null
      });
      handleFileRejectionErrors(fileRejections);
      const file = e[0];
      if (file) {
        const {
          name,
          size
        } = file;
        removeDashboardQr();
        setFile({
          file,
          name,
          size: bytesToSize(size),
          fromOther: false
        });
        if (!isLoadingUploadFile && upload < 1) {
          upload++;
          const formData = new FormData();
          formData.append("pdf", file);
          formData.append("type", forRender.pdf.toLowerCase());
          uploadFile(formData);
        }
      }
    },
    onDragEnter: () => {
      setIsDragging(true);
    },
    onDragLeave: () => {
      setIsDragging(false);
    },
    onDropRejected: () => {
      setIsDragging(false);
    },
    onDropAccepted: () => {
      setIsDragging(false);
    }
  });
  useEffect(() => {
    if (!isLoadingUploadFile) {
      if (uploadFileData) {
        setUserPdf({
          id: uploadFileData?.data["pdf"].id,
          pdf: uploadFileData?.data["pdf"].path,
          pdf_name: uploadFileData?.data["pdf"].originalName,
          pdf_size: bytesToSize(Number(uploadFileData?.data["pdf"].size))
        });
        setFiles({
          pdf: uploadFileData?.data["pdf"].id
        });
      }
    }
  }, [isLoadingUploadFile]);
  useEffect(() => {
    if (!file?.fromOther) {
      if (file?.file && uploadFileData?.data["pdf"]?.id) {
        mutateHomeQr({
          params: {
            files: {
              pdf: data.id
            },
            config
          }
        });
      }
    }
  }, [data]);
  const pdfFileName = useMemo(() => {
    if (data.pdf_name) return everyTrue(isMobile, data.pdf_name.length > 20) ? createShortPdfName(data.pdf_name, 20) : data.pdf_name;
    return null;
  }, [isMobile, data.pdf_name]);
  useEffect(() => {
    setLoadingState(() => ({
      name: isMobile ? createShortPdfName(file?.file?.name || '', 20) : file?.file?.name || '',
      size: file?.file?.size ? bytesToSize(Number(file?.file?.size)) : ''
    }));
  }, [file]);

  // FIXME CONDITION DEPS
  return <div className={classes.Wrapper} data-sentry-component="Pdf" data-sentry-source-file="Pdf.tsx">
      <div className={classes.Header}>
        <span className={classes.step}>1</span>
        <p className={classes.TopTitle}>{t("title")}</p>
      </div>
      <div className={classes.Title}>
        <p>{t("description")}</p>
      </div>

      <IFThen condition={isLoadingUploadFile} Then={<div className={cn(classes.ShowMp3Data, {
      [classes.success]: isLoadingUploadFile
    })}>
            <div className={classes.File} {...getRootProps()}>
              {isLoadingUploadFile ? isMobile ? <div className={classes.fileLoadingScreen}>
                    <div>
                      <div className={classes.ShowMp3Title}>
                        <img src="types/pdf.svg" alt={"pdf"} />
                        <p>{loadingState.name}</p>
                      </div>
                      <div className={classes.ShowMp3Upload}>
                        <div className={classes.ShowMp3UploadTitle}>
                          <div className={classes.ChangeFile}>
                            <span>
                              {t("fileSize")}: {loadingState.size}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <LoadingDots bgColor="#A1A1A1" color="#575757" />
                  </div> : <div className={classes.fileLoadingScreen}>
                    <div className={classes.fileLoadingInfo}>
                      <div className={classes.ShowMp3Title}>
                        <img src="types/pdf.svg" alt={"pdf"} />
                        <p>{loadingState.name}</p>
                      </div>
                      <span>{t("fileSize")}: {loadingState.size}</span>
                    </div>
                    <LoadingDots bgColor="#A1A1A1" color="#575757" />
                  </div> : null}
            </div>
          </div>} Else={<IFThen condition={!!data.pdf} Then={<div {...getRootProps()} className={cn(classes.ShowMp3Data, {
      [classes.success]: !!data.pdf,
      [classes.DragArea]: isDragging
    })}>
                {isDragging ? <div className={classes.DraggingAfterUpload}>
                    <FiUploadCloud size={16} />
                    <p className={classes.DragText}>{t2("dropHere")}</p>
                  </div> : <>
                    <div>
                      <div className={classes.ShowMp3Title}>
                        <img src="types/pdf.svg" alt={"pdf"} />
                        <p>{pdfFileName}</p>
                      </div>
                      <div className={classes.ShowMp3Upload}>
                        <div className={classes.ShowMp3UploadTitle}>
                          <div className={classes.ChangeFile}>
                            <span>
                              {t("fileSize")}: {data?.pdf_size}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={classes.File}>
                      <>
                        <When condition={!isMobile}>
                          <HiOutlineRefresh size={15} />
                        </When>
                        {t("change")}
                        <input {...getInputProps()} />
                      </>
                    </div>
                  </>}
              </div>} Else={<>
                <div className={cn(classes.DropBox, {
        [classes.validErrorSetupDashed]: hasTrue(!!fileError?.length, hasFileUploadErr),
        [classes.isDragging]: isDragging
      })}>
                  <div {...getRootProps()} className={isDragging ? classes.DragArea : classes.DropArea}>
                    {isDragging ? <div className={classes.DraggingContainer}>
                            <FiUploadCloud size={16} />
                            <p className={classes.DragText}>{t2("dropHere")}</p>
                          </div> : <div className="padBox">
                            <p className={classes.AddMp3}>
                              <AiOutlineFilePdf size={17} />
                              <span>{t("input.placeholder")}</span>
                              <input {...getInputProps()} />
                            </p>
                            <span className={classes.FileSize}>{t("input.label")}</span>
                          </div>}
                    <When condition={isLoadingUploadFile}>
                      <LoadingDots bgColor="#A1A1A1" />
                    </When>
                  </div>
                </div>

                <When condition={everyTrue(invalidType, !largeFile)}>
                  <Error mt={8}>
                    <BiErrorCircle color="#F25A29" size={16} />
                    {invalidType}
                  </Error>
                </When>

                <When condition={!!largeFile}>
                  <Error mt={8}>
                    <BiErrorCircle color="#F25A29" size={16} />
                    {largeFile}
                  </Error>
                </When>

                <When condition={!!errors?.["files.pdf"]}>
                  <HomePageError withIcon={false} errors={errors?.["files.pdf"]} />
                </When>
              </>} />} data-sentry-element="IFThen" data-sentry-source-file="Pdf.tsx" />
    </div>;
};
export default Pdf;